import { createContext, type ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridRow } from "../columns/types";

type DataGridRowOpen<TData extends RowData> = (
    row: DataGridRow<TData>,
    columnId: string | undefined,
) => void;

/**
 * Rows that stand for an item not created yet. The draft column's cell holds
 * a create input; the row can't be selected or opened, and its list cells
 * are read-only.
 */
export interface DataGridDraftRowOptions<TData> {
    /** Rows that stand for an item not created yet. */
    isDraft: (row: TData) => boolean;
    /** The `col.text` column whose cell holds the create input. */
    columnId: string;
    /** The input's placeholder and accessible name: "Create task". */
    placeholder: string;
    /** Enter, or leaving the input with text: the trimmed text. */
    onCreate: (draft: { row: TData; value: string }) => void;
    /** Escape, or leaving the input empty. */
    onCancel: (row: TData) => void;
    /** Focus the input when the row mounts. */
    shouldAutoFocus?: (row: TData) => boolean;
}

/** Inside a draft row's cells: the draft column and its create input. */
export interface DraftRowCell {
    columnId: string;
    input: ReactNode;
}

/** Set only around a draft row's cells, so other rows read the default. */
export const DraftRowContext = createContext<DraftRowCell | null>(null);

/** `onRowOpen` that skips draft rows; `options` unchanged without drafts or `onRowOpen`. */
export const withDraftRowOpenGuard = <
    TData extends RowData,
    TOptions extends {
        draftRows?: DataGridDraftRowOptions<TData>;
        onRowOpen?: DataGridRowOpen<TData>;
    },
>({
    options,
    isDraft,
}: {
    options: TOptions;
    isDraft: (row: TData) => boolean;
}): TOptions => {
    const { draftRows, onRowOpen } = options;
    if (!draftRows || !onRowOpen) {
        return options;
    }
    const openSavedRow: DataGridRowOpen<TData> = (row, columnId) => {
        if (!isDraft(row.original)) {
            onRowOpen(row, columnId);
        }
    };
    return { ...options, onRowOpen: openSavedRow };
};
